import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadAttachment } from '@/api/attachments';
import { extractTopic } from '@/api/extractTopic';
import type { ExtractResponse } from '@/types/extractedTopic';

export type BulkItemPhase =
  | 'queued'
  | 'uploading'
  | 'extracting'
  | 'ready'
  | 'saved'
  | 'skipped'
  | 'failed';

export interface BulkItem {
  id: string;
  file: File;
  phase: BulkItemPhase;
  attachmentId?: string;
  result?: ExtractResponse;
  errorMessage?: string;
  previewUrl?: string;
}

const MAX_PARALLEL = 3;

/**
 * Manages a queue of bulk flyer extractions. Runs at most MAX_PARALLEL
 * uploads + extractions concurrently. Items advance through phases:
 * queued → uploading → extracting → ready (awaits manual review) →
 * saved (after review form submit) or skipped.
 *
 * Implementation: a ref-tracked set of in-flight ids drives a pump()
 * helper that schedules the next queued item whenever one completes.
 */
export function useBulkExtraction() {
  const [items, setItems] = useState<BulkItem[]>([]);
  const inFlight = useRef<Set<string>>(new Set());
  // Mirror of items so pump() can read the latest queue synchronously
  // without depending on a re-render.
  const itemsRef = useRef<BulkItem[]>([]);
  // Held in a ref so processOne can call pump without forming a
  // useCallback dependency cycle.
  const pumpRef = useRef<() => void>(() => {});

  const update = useCallback((id: string, patch: Partial<BulkItem>) => {
    setItems((prev) => {
      const next = prev.map((it) => (it.id === id ? { ...it, ...patch } : it));
      itemsRef.current = next;
      return next;
    });
  }, []);

  const processOne = useCallback(
    async (item: BulkItem) => {
      const previewUrl = URL.createObjectURL(item.file);
      update(item.id, { phase: 'uploading', previewUrl });
      try {
        const att = await uploadAttachment(item.file);
        update(item.id, { phase: 'extracting', attachmentId: att.id });
        const source = item.file.type === 'application/pdf' ? 'pdf' : 'image';
        const res = await extractTopic({ source, attachment_id: att.id });
        if (res.status === 'failed') {
          update(item.id, { phase: 'failed', errorMessage: res.message ?? res.error });
        } else {
          update(item.id, { phase: 'ready', result: res });
        }
      } catch (e) {
        update(item.id, { phase: 'failed', errorMessage: (e as Error).message });
      } finally {
        inFlight.current.delete(item.id);
        pumpRef.current();
      }
    },
    [update],
  );

  const pump = useCallback(() => {
    while (inFlight.current.size < MAX_PARALLEL) {
      const next = itemsRef.current.find(
        (it) => it.phase === 'queued' && !inFlight.current.has(it.id),
      );
      if (!next) break;
      // Mark as in-flight synchronously to prevent double-picking before
      // processOne mutates phase via setState.
      inFlight.current.add(next.id);
      void processOne(next);
    }
  }, [processOne]);

  // Keep the ref pointing at the latest pump closure.
  useEffect(() => {
    pumpRef.current = pump;
  }, [pump]);

  function addFiles(files: File[]) {
    const newItems: BulkItem[] = files.map((f) => ({
      id: crypto.randomUUID(),
      file: f,
      phase: 'queued',
    }));
    setItems((prev) => {
      const next = [...prev, ...newItems];
      itemsRef.current = next;
      return next;
    });
    setTimeout(pump, 0);
  }

  function markSaved(id: string) {
    update(id, { phase: 'saved' });
  }

  function markSkipped(id: string) {
    update(id, { phase: 'skipped' });
  }

  function reset() {
    for (const it of itemsRef.current) {
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    }
    inFlight.current.clear();
    itemsRef.current = [];
    setItems([]);
  }

  return { items, addFiles, markSaved, markSkipped, reset };
}
